import { useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
  FormInstance,
  Popover,
  Button,
  Tooltip,
} from 'antd';
import { isEmpty } from 'lodash';
import { useAddressQuery, useWindowSize } from '@/hooks';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import ZipFormField from '@/components/_common/ZipFormField';
import { CompaniesInfiniteSelectField, InputText } from '@/components/_common';
import {
  BankAccount,
  CreateBankAccountFormValues,
} from '@/core/domain/enterprise/BankAccounts/BankAccounts.types';
import { bankConfig } from '@/core/domain/financial/Boletos/Boletos.dictionary';
import { Link } from 'react-router-dom';
import { ExternalLink, Info } from 'react-feather';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import BanksInfiniteSelectField from '@/components/_common/BanksInfiniteSelectField';

const { Title } = Typography;

const defaultEmptyFieldError = {
  required: true,
  message: <FormattedMessage id="commons.form.errors.requiredField" />,
};

interface Props {
  form: FormInstance<CreateBankAccountFormValues>;
  bankAccount?: BankAccount;
}

function BankAccountForm({ form, bankAccount }: Props) {
  const intl = useIntl();
  const { isMobile } = useWindowSize();
  const { state, city, zipCode } = useAddressQuery();

  const watchZip = Form.useWatch(['accountAddress', 'zip'], form);
  const bankIntegration = Form.useWatch(['bank', 'integration'], form);
  const bankCode = Form.useWatch(['bank', 'code'], form);

  useEffect(() => {
    if (!isEmpty(zipCode.data) && zipCode.data) {
      const bankAccountAddress = bankAccount?.accountAddress;
      state.setSelectedStateCode(zipCode.data?.state);

      form.setFieldsValue({
        accountAddress: {
          cityId: zipCode.data.cityId ?? bankAccountAddress?.cityId,
          street: !!zipCode.data.street
            ? zipCode.data.street
            : bankAccountAddress?.street,
          district: !!zipCode.data.district
            ? zipCode.data.district
            : bankAccountAddress?.district,
          state: zipCode.data.state ?? bankAccountAddress?.state,
        },
      });
    }
  }, [zipCode.data]);

  useEffect(() => {
    if (watchZip && watchZip.length >= 8) {
      zipCode.setSelectedZipCode(watchZip);
    } else {
      zipCode.setSelectedZipCode(undefined);

      form.setFieldsValue({
        accountAddress: {
          cityId: undefined,
          street: undefined,
          district: undefined,
          state: undefined,
        },
      });

      return;
    }
  }, [watchZip]);

  const bankMapFields = bankCode ? bankConfig[bankCode] : null;

  return (
    <Form layout="vertical" form={form} name="bankAccountGeneralForm">
      <Row gutter={[16, 9]}>
        <Col span={24} md={12} lg={8}>
          <FloatingFormItem
            label={intl.formatMessage({ id: 'bankAccounts.form.bank' })}
            name={['bank', 'id']}
            rules={[defaultEmptyFieldError]}
          >
            <BanksInfiniteSelectField
              disabled={
                !hasPermissions([Authorities.REGISTER_BANK_ACCOUNT_VIEW_BANK])
              }
              onBankSelected={(bank) => {
                form.setFieldsValue({ bank });
              }}
            />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={3}>
          <Form.Item hidden name={['bank', 'integration']} noStyle />

          <FloatingFormItem
            label={intl.formatMessage({ id: 'bankAccounts.form.bankCode' })}
            name={['bank', 'code']}
            rules={[defaultEmptyFieldError]}
          >
            <Input disabled />
          </FloatingFormItem>
        </Col>

        <Popover
          overlayStyle={{ maxWidth: isMobile ? '300px' : '400px' }}
          trigger="click"
          placement={isMobile ? 'top' : 'left'}
          content={
            <div>
              {`Se sua empresa não está aparecendo na listagem, preencha os seguintes campos obrigatórios: Telefone, Email e Endereço Completo`}

              <Link to={'/app/registry/companies'} target="_blank">
                <Button icon={<ExternalLink size={10} />} type="link">
                  Ir até a Listagem de Empresas
                </Button>
              </Link>
            </div>
          }
          title="Atenção!"
        >
          {bankIntegration && (
            <Col span={24} md={12} lg={7}>
              <FloatingFormItem label="Cedente da Conta" name="companyId">
                <CompaniesInfiniteSelectField
                  allowClear
                  queryFilter={{ billingIntegration: true }}
                />
              </FloatingFormItem>
            </Col>
          )}
        </Popover>

        {!!bankMapFields?.fields.codeCompanyBank && (
          <Tooltip
            trigger="click"
            title={bankMapFields?.fields.codeCompanyBank?.helpMessage}
          >
            <Col span={24} md={12} lg={6} xxl={4}>
              <FloatingFormItem
                label="Codigo da empresa no banco"
                name="codeCompanyBank"
                rules={
                  bankMapFields?.fields.codeCompanyBank?.required
                    ? [defaultEmptyFieldError]
                    : []
                }
              >
                <Input maxLength={20} showCount />
              </FloatingFormItem>
            </Col>
          </Tooltip>
        )}

        <Col span={24} md={12} lg={6} xxl={bankIntegration ? 8 : 14}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'bankAccounts.form.description',
            })}
            name="description"
          >
            <Input maxLength={50} showCount />
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12} lg={6} xxl={4}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'bankAccounts.form.accountOperation',
            })}
            name="accountOperation"
          >
            <InputText onlyNumber maxLength={3} />
          </FloatingFormItem>
        </Col>

        {/* Agencia */}
        <Col
          span={24}
          md={12}
          lg={4}
          hidden={!!bankMapFields && !bankMapFields?.fields.bankBranch?.visible}
        >
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'bankAccounts.form.bankBranch',
            })}
            name="bankBranch"
            rules={
              !!bankMapFields
                ? [
                    {
                      type: 'string',
                      max: bankMapFields?.fields.bankBranch?.maxLength,
                      message: `Quantidade máxima: ${bankMapFields?.fields.bankBranch?.maxLength}`,
                    },
                    ...(bankMapFields?.fields.bankBranch?.required
                      ? [defaultEmptyFieldError]
                      : []),
                  ]
                : [defaultEmptyFieldError]
            }
          >
            <InputText onlyNumber maxLength={15} />
          </FloatingFormItem>
        </Col>

        {!!bankMapFields?.fields.bankBranch?.helpMessage && (
          <Col>
            <Tooltip title={bankMapFields?.fields.bankBranch?.helpMessage}>
              <Info size={20} cursor={'pointer'} />
            </Tooltip>
          </Col>
        )}

        {/* Agencia DV */}
        <Col
          span={24}
          md={12}
          lg={2}
          hidden={
            !!bankMapFields &&
            !bankMapFields?.fields.branchVerificationNumber?.visible
          }
        >
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'bankAccounts.form.branchVerificationNumber',
            })}
            name="branchVerificationNumber"
            rules={
              !!bankMapFields
                ? [
                    {
                      type: 'string',
                      max: bankMapFields?.fields.branchVerificationNumber
                        ?.maxLength,
                      message: `Quantidade máxima: ${bankMapFields?.fields.branchVerificationNumber?.maxLength}`,
                    },
                    ...(bankMapFields?.fields.branchVerificationNumber?.required
                      ? [defaultEmptyFieldError]
                      : []),
                  ]
                : []
            }
          >
            <InputText onlyNumber maxLength={3} />
          </FloatingFormItem>
        </Col>

        {/* Conta  */}
        <Col
          span={24}
          md={12}
          lg={4}
          hidden={!!bankMapFields && !bankMapFields?.fields.number?.visible}
        >
          <FloatingFormItem
            label={intl.formatMessage({ id: 'bankAccounts.form.number' })}
            name="number"
            rules={
              !!bankMapFields
                ? [
                    {
                      type: 'string',
                      max: bankMapFields?.fields.number?.maxLength,
                      message: `Quantidade máxima: ${bankMapFields?.fields.number?.maxLength}`,
                    },
                    ...(bankMapFields?.fields.number?.required
                      ? [defaultEmptyFieldError]
                      : []),
                  ]
                : [defaultEmptyFieldError]
            }
          >
            <InputText onlyNumber maxLength={15} />
          </FloatingFormItem>
        </Col>

        {/* Conta DV  */}
        <Col
          span={24}
          md={12}
          lg={2}
          hidden={
            !!bankMapFields &&
            !bankMapFields?.fields.verificationNumber?.visible
          }
        >
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'bankAccounts.form.verificationNumber',
            })}
            name="verificationNumber"
            rules={
              !!bankMapFields
                ? [
                    {
                      type: 'string',
                      max: bankMapFields?.fields.verificationNumber?.maxLength,
                      message: `Quantidade máxima: ${bankMapFields?.fields.verificationNumber?.maxLength}`,
                    },
                    ...(bankMapFields?.fields.verificationNumber?.required
                      ? [defaultEmptyFieldError]
                      : []),
                  ]
                : [defaultEmptyFieldError]
            }
          >
            <Input maxLength={3} />
          </FloatingFormItem>
        </Col>

        <Tooltip
          trigger="click"
          title={bankMapFields?.fields.beneficiaryCode?.helpMessage}
        >
          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label="Código beneficiário"
              name="beneficiaryCode"
              rules={
                bankMapFields?.fields.beneficiaryCode?.required
                  ? [defaultEmptyFieldError]
                  : []
              }
            >
              <Input />
            </FloatingFormItem>
          </Col>
        </Tooltip>
      </Row>

      <div>
        <Title level={3} style={{ margin: '16px 0 24px 0' }}>
          <FormattedMessage id="bankAccounts.form.address" />
        </Title>
        <Row gutter={[16, 8]}>
          <Col span={24} md={12} lg={6} xxl={4}>
            <ZipFormField
              disable={false}
              label={
                <FormattedMessage id="bankAccounts.form.address.zipCode" />
              }
              fieldName={['accountAddress', 'zip']}
              required={false}
            />
          </Col>

          <Col span={24} md={12} lg={6} xxl={11}>
            <FloatingFormItem
              label={<FormattedMessage id="bankAccounts.form.address.street" />}
              name={['accountAddress', 'street']}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={3}>
            <FloatingFormItem
              label={<FormattedMessage id="bankAccounts.form.address.number" />}
              name={['accountAddress', 'number']}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6}>
            <FloatingFormItem
              label={
                <FormattedMessage id="bankAccounts.form.address.district" />
              }
              name={['accountAddress', 'district']}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={4}>
            <FloatingFormItem
              label={<FormattedMessage id="bankAccounts.form.address.uf" />}
              name={['accountAddress', 'state']}
            >
              <Select
                disabled
                allowClear
                style={{ width: '100%' }}
                loading={state.isFetching}
                onChange={(e) => {
                  form.setFieldsValue({
                    accountAddress: { cityId: undefined },
                  });
                  state.setSelectedStateCode(e);
                }}
                options={state.list.map((s) => ({
                  value: s.code,
                  label: s.code,
                }))}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={8}>
            <FloatingFormItem
              label={<FormattedMessage id="bankAccounts.form.address.city" />}
              name={['accountAddress', 'cityId']}
            >
              <Select
                disabled
                allowClear
                style={{ width: '100%' }}
                loading={city.isFetching}
                options={city.list.map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={12} lg={6} xxl={12}>
            <FloatingFormItem
              label={
                <FormattedMessage id="bankAccounts.form.address.complement" />
              }
              name={['accountAddress', 'complement']}
            >
              <Input maxLength={255} showCount />
            </FloatingFormItem>
          </Col>
        </Row>
      </div>
    </Form>
  );
}

export default BankAccountForm;
